import { Box, Divider, Drawer, Stack, useMediaQuery } from "@mui/material";
import { Scrollbar } from "@/components/scrollbar";
import { items } from "./config";
import { SideNavItem } from "./side-nav-item";
import { useLocation, Link as ReactLink } from "react-router-dom";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { selectUser } from "@/app/features/auth/authSlice";
import Roles from "@/constants/roles";

export const SideNav = (props) => {
    const { open, onClose } = props;
    const { pathname } = useLocation();
    const user = useSelector(selectUser);
    const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"));

    // Filter menu items based on user role
    const filteredItems = items.filter((item) => {
        // Companies should only be visible to Super Admin
        if (item.title === "Companies") {
            return user?.role === Roles.SUPER_ADMIN;
        }
        // Submitted Forms should only be visible to Admin (not Super Admin)
        if (item.title === "Submitted Forms") {
            return user?.role === Roles.ADMIN;
        }
        // Users (guides) should only be visible to Admin (not Super Admin)
        if (item.title === "Users") {
            return user?.role === Roles.ADMIN;
        }
        // All other items are visible to both Admin and Super Admin
        return true;
    });

    const content = (
        <Scrollbar
            sx={{
                height: "100%",
                "& .simplebar-content": {
                    height: "100%",
                },
                "& .simplebar-scrollbar:before": {
                    background: "neutral.400",
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                }}
            >
                <Box sx={{ p: 3 }}>
                    <Box
                        component={ReactLink}
                        to="/"
                        sx={{
                            display: "inline-flex",
                        }}
                    >
                        <img
                            style={{
                                maxWidth: "100%",
                            }}
                            src="/assets/logos/opdox_1.png"
                            alt="beep"
                        />
                    </Box>
                </Box>
                <Divider sx={{ borderColor: "neutral.700" }} />
                <Box
                    component="nav"
                    sx={{
                        flexGrow: 1,
                        px: 2,
                        py: 3,
                    }}
                >
                    <Stack
                        component="ul"
                        spacing={0.5}
                        sx={{
                            listStyle: "none",
                            p: 0,
                            m: 0,
                        }}
                    >
                        {filteredItems.map((item) => {
                            // For Dashboard (path === "/"), only match exactly "/"
                            // For other paths, check if pathname starts with the path
                            let active = false;
                            if (item.path) {
                                if (item.path === "/") {
                                    active = pathname === "/";
                                } else {
                                    // For other paths, check if pathname starts with the path
                                    // Use exact match or pathname starts with path + "/" to avoid partial matches
                                    active = pathname === item.path || pathname.startsWith(item.path + "/");
                                }
                            }
                            return (
                                <SideNavItem
                                    active={active}
                                    disabled={item?.disabled}
                                    external={item?.external}
                                    icon={item.icon}
                                    key={item.title}
                                    path={item.path}
                                    title={item.title}
                                />
                            );
                        })}
                    </Stack>
                </Box>
            </Box>
        </Scrollbar>
    );

    if (lgUp) {
        return (
            <Drawer
                anchor="left"
                open
                PaperProps={{
                    sx: {
                        backgroundColor: (theme) => theme.palette.delftBlue[500],
                        color: "common.white",
                        width: 280,
                    },
                }}
                variant="permanent"
            >
                {content}
            </Drawer>
        );
    }

    return (
        <Drawer
            anchor="left"
            onClose={onClose}
            open={open}
            PaperProps={{
                sx: {
                    backgroundColor: (theme) => theme.palette.delftBlue[500],
                    color: "common.white",
                    width: 280,
                },
            }}
            sx={{ zIndex: (theme) => theme.zIndex.appBar + 100 }}
            variant="temporary"
        >
            {content}
        </Drawer>
    );
};

SideNav.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};
